import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Contact } from '../types';

interface NewConversationModalProps {
  contacts: Contact[];
  onClose: () => void;
  onCreateConversation: (participantIds: string[], conversationName?: string) => void;
}

const NewConversationModal = ({
  contacts,
  onClose,
  onCreateConversation,
}: NewConversationModalProps) => {
  const [conversationType, setConversationType] = useState<'private' | 'group'>('private');
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [groupName, setGroupName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClassName, setSelectedClassName] = useState<string>('');

  const normalizeLabel = (value?: string) => (value ?? '').replace(/\s+/g, ' ').trim();
  const classKey = (value?: string) => normalizeLabel(value).toLocaleLowerCase('ro-RO');

  const getRoleLabelSingle = (role: string) => {
    switch (role) {
      case 'admin':
        return 'administrator';
      case 'secretariat':
        return 'secretariat';
      case 'teacher':
        return 'profesor';
      case 'student':
        return 'elev';
      case 'parent':
        return 'părinte';
      default:
        return role;
    }
  };

  const getParentChildrenLabel = (contact: Contact) => {
    const children = contact.children ?? [];
    if (children.length === 0) return null;
    return children
      .map((c) => `${c.name}${c.classroomName ? ` (${c.classroomName})` : ''}`)
      .join(', ');
  };

  const renderParentChildren = (contact: Contact) => {
    const children = contact.children ?? [];
    if (children.length === 0) return null;
    return (
      <div className="mt-0.5 text-xs text-muted-foreground leading-4">
        <div className="font-medium text-muted-foreground/90">Părinte:</div>
        <div className="mt-0.5 space-y-0.5">
          {children.map((c) => (
            <div key={c.id} className="whitespace-normal break-words">
              {c.name}
              {c.classroomName ? <span className="opacity-80"> ({c.classroomName})</span> : null}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const filteredContacts = contacts.filter((contact) =>
    contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (contact.role === 'parent' &&
      (contact.children ?? []).some((c) => c.name.toLowerCase().includes(searchQuery.toLowerCase())))
  );

  const getClassLabel = (value?: string) => normalizeLabel(value) || 'Fără clasă';

  const parentClassGroup = (contact: Contact) => {
    const classes = (contact.children ?? [])
      .map((c) => c.classroomName?.trim())
      .filter(Boolean) as string[];
    const unique = Array.from(new Set(classes));
    if (unique.length === 0) return 'Fără clasă';
    if (unique.length === 1) return unique[0];
    return 'Mai multe clase';
  };

  const allClassNames = useMemo(() => {
    const classNames = new Map<string, string>();
    const addClassName = (value?: string) => {
      const label = getClassLabel(value);
      if (label === 'Fără clasă') return;
      const key = classKey(label);
      if (!classNames.has(key)) classNames.set(key, label);
    };
    for (const c of contacts) {
      if (c.role === 'student') {
        addClassName(c.grade || c.department);
      }
      if (c.role === 'parent') {
        for (const child of c.children ?? []) {
          addClassName(child.classroomName);
        }
      }
    }
    return Array.from(classNames.entries())
      .sort(([, a], [, b]) => a.localeCompare(b, 'ro-RO'))
      .map(([key, label]) => ({ key, label }));
  }, [contacts]);

  const classOptions = useMemo(
    () => [
      { value: '', label: 'Selectează o clasă' },
      ...allClassNames.map(({ key, label }) => ({ value: key, label })),
    ],
    [allClassNames]
  );

  const handleToggleContact = (contactId: string) => {
    setSelectedContacts((prev) => {
      const isSelected = prev.includes(contactId);
      if (conversationType === 'private') {
        return isSelected ? [] : [contactId];
      }
      return isSelected ? prev.filter((id) => id !== contactId) : [...prev, contactId];
    });
  };

  const addStudentsOfClass = (className: string) => {
    const key = classKey(className);
    const ids = contacts
      .filter((c) => c.role === 'student' && classKey(c.grade || c.department) === key)
      .map((c) => c.id);
    setSelectedContacts((prev) => Array.from(new Set([...prev, ...ids])));
  };

  const addParentsOfClass = (className: string) => {
    const key = classKey(className);
    const ids = contacts
      .filter(
        (c) =>
          c.role === 'parent' &&
          (c.children ?? []).some((ch) => classKey(ch.classroomName) === key)
      )
      .map((c) => c.id);
    setSelectedContacts((prev) => Array.from(new Set([...prev, ...ids])));
  };

  useEffect(() => {
    if (conversationType === 'private' && selectedContacts.length > 1) {
      setSelectedContacts((prev) => prev.slice(0, 1));
    }
  }, [conversationType, selectedContacts.length]);

  useEffect(() => {
    if (conversationType !== 'group') {
      setSelectedClassName('');
    }
  }, [conversationType]);

  const handleCreate = () => {
    if (selectedContacts.length === 0) return;

    if (conversationType === 'group' && !groupName.trim()) {
      return;
    }

    onCreateConversation(
      conversationType === 'private' ? selectedContacts.slice(0, 1) : selectedContacts,
      conversationType === 'group' ? groupName : undefined
    );
    onClose();
  };

  const canCreate =
    selectedContacts.length > 0 &&
    (conversationType === 'private' || (conversationType === 'group' && groupName.trim()));

  const groupedContacts = useMemo(() => {
    const byRole: Record<string, Contact[]> = {
      admin: [],
      secretariat: [],
      teacher: [],
      student: [],
      parent: [],
    };
    for (const c of filteredContacts) {
      byRole[c.role] = [...(byRole[c.role] ?? []), c];
    }
    return byRole;
  }, [filteredContacts]);

  const groupBy = (items: Contact[], keyFn: (c: Contact) => string) => {
    const map = new Map<string, Contact[]>();
    for (const c of items) {
      const key = keyFn(c);
      map.set(key, [...(map.get(key) ?? []), c]);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b, 'ro-RO'));
  };

  const sections = useMemo(() => {
    const sortByName = (a: Contact, b: Contact) => a.name.localeCompare(b.name, 'ro-RO');
    const admins = groupedContacts.admin.slice().sort(sortByName);
    const secretariat = groupedContacts.secretariat.slice().sort(sortByName);
    const teachers = groupedContacts.teacher.slice().sort(sortByName);
    const studentsByClass = groupBy(groupedContacts.student, (c) => getClassLabel(c.grade));
    const parentsByClass = groupBy(groupedContacts.parent, (c) => parentClassGroup(c));

    return [
      { key: 'admin', title: 'Administratori', items: admins },
      { key: 'secretariat', title: 'Secretariat', items: secretariat },
      { key: 'teacher', title: 'Profesori', items: teachers },
      ...studentsByClass.map(([cls, items]) => ({ key: `student:${cls}`, title: `Elevi • ${cls}`, items })),
      ...parentsByClass.map(([cls, items]) => ({ key: `parent:${cls}`, title: `Părinți • ${cls}`, items })),
    ].filter((s) => s.items.length > 0);
  }, [groupedContacts]);

  return (
    <div className="fixed inset-0 z-[1100] flex items-start justify-center px-4 py-6 sm:py-8 overflow-y-auto bg-black/50 animate-fade-in">
      <div className="w-full max-w-2xl max-h-[calc(100vh-3rem)] sm:max-h-[calc(100vh-4rem)] bg-card rounded-lg shadow-lg animate-scale-in flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-3 sm:p-4 border-b border-border flex-shrink-0">
          <h2 className="text-xl font-semibold text-foreground">Conversație nouă</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors duration-150"
            aria-label="Închide fereastra"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-4 space-y-4">
          <div className="flex gap-4">
            <button
              onClick={() => setConversationType('private')}
              className={`flex-1 p-4 rounded-lg border-2 transition-all duration-150 ${
                conversationType === 'private' ?'border-primary bg-primary/10' :'border-border hover:border-primary/50'
              }`}
            >
              <div className="flex items-center justify-center mb-2">
                <Icon
                  name="User"
                  size={24}
                  className={conversationType === 'private' ? 'text-primary' : 'text-muted-foreground'}
                />
              </div>
              <p className="text-sm font-medium text-foreground">Chat privat</p>
              <p className="text-xs text-muted-foreground mt-1">Conversație 1-la-1</p>
            </button>

            <button
              onClick={() => setConversationType('group')}
              className={`flex-1 p-4 rounded-lg border-2 transition-all duration-150 ${
                conversationType === 'group' ?'border-primary bg-primary/10' :'border-border hover:border-primary/50'
              }`}
            >
              <div className="flex items-center justify-center mb-2">
                <Icon
                  name="Users"
                  size={24}
                  className={conversationType === 'group' ? 'text-primary' : 'text-muted-foreground'}
                />
              </div>
              <p className="text-sm font-medium text-foreground">Chat de grup</p>
              <p className="text-xs text-muted-foreground mt-1">Mai mulți participanți</p>
            </button>
          </div>

          {conversationType === 'group' && (
            <div className="space-y-4">
              <Input
                label="Nume grup"
                type="text"
                placeholder="Introdu numele grupului..."
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                required
              />

              <div className="rounded-md border border-border p-3 sm:p-4 bg-background">
                <p className="text-sm font-medium text-foreground mb-3">Adaugă rapid</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 sm:gap-3 items-end">
                  <Select
                    label="Clasă"
                    options={classOptions}
                    value={selectedClassName}
                    onChange={(v) =>
                      setSelectedClassName(typeof v === 'string' || typeof v === 'number' ? String(v) : '')
                    }
                  />
                  <Button
                    variant="outline"
                    disabled={!selectedClassName}
                    onClick={() => selectedClassName && addStudentsOfClass(selectedClassName)}
                  >
                    Adaugă elevii clasei
                  </Button>
                  <Button
                    variant="outline"
                    disabled={!selectedClassName}
                    onClick={() => selectedClassName && addParentsOfClass(selectedClassName)}
                  >
                    Adaugă părinții clasei
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Selectează participanți
            </label>
            <Input
              type="search"
              placeholder="Caută contacte..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="mb-3"
            />

            <div className="max-h-[34vh] lg:max-h-64 overflow-y-auto border border-border rounded-md">
              {filteredContacts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <Icon name="Users" size={48} className="text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground">Nu s-au găsit contacte</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {sections.map((section) => (
                    <div key={section.key}>
                      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-muted/40 border-b border-border">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          {section.title} ({section.items.length})
                        </p>
                        {conversationType === 'group' &&
                          (section.key.startsWith('student:') || section.key.startsWith('parent:')) && (() => {
                            const className = section.key.split(':')[1] || '';
                            if (!className || className === 'Fără clasă' || className === 'Mai multe clase') return null;
                            return (
                              <button
                                type="button"
                                onClick={() => {
                                  if (section.key.startsWith('student:')) addStudentsOfClass(className);
                                  if (section.key.startsWith('parent:')) addParentsOfClass(className);
                                }}
                                className="text-xs text-primary hover:underline"
                              >
                                Selectează toți
                              </button>
                            );
                          })()}
                      </div>
                      <div className="divide-y divide-border">
                        {section.items.map((contact) => (
                          <label
                            key={contact.id}
                            className="flex items-start gap-3 p-3 hover:bg-muted cursor-pointer transition-colors duration-150"
                          >
                            <Checkbox
                              checked={selectedContacts.includes(contact.id)}
                              onChange={() => handleToggleContact(contact.id)}
                            />

                            <div className="relative flex-shrink-0 mt-0.5">
                              {contact.avatar ? (
                                <Image
                                  src={contact.avatar}
                                  alt={`${contact.name} avatar`}
                                  className="w-10 h-10 rounded-full object-cover"
                                />
                              ) : (
                                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary text-primary-foreground text-sm font-medium">
                                  {contact.name.charAt(0).toUpperCase()}
                                </div>
                              )}
                              {contact.isOnline && (
                                <div className="absolute bottom-0 right-0 w-3 h-3 bg-success rounded-full border-2 border-card" />
                              )}
                            </div>

                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">
                                {contact.name}
                              </p>
                              <div className="text-xs text-muted-foreground">
                                <div className="flex items-center gap-2">
                                  <span className="capitalize">{getRoleLabelSingle(contact.role)}</span>
                                  {contact.department && contact.role !== 'parent' && (
                                    <>
                                      <span>•</span>
                                      <span>{contact.department}</span>
                                    </>
                                  )}
                                  {contact.grade && contact.role === 'student' && (
                                    <>
                                      <span>•</span>
                                      <span>{contact.grade}</span>
                                    </>
                                  )}
                                </div>
                                {contact.role === 'parent' && renderParentChildren(contact)}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {selectedContacts.length > 0 && (
              <div className="mt-3 p-3 bg-muted rounded-md">
                <p className="text-sm text-foreground">
                  <span className="font-medium">{selectedContacts.length}</span>{' '}
                  {selectedContacts.length === 1 ? 'participant selectat' : 'participanți selectați'}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-3 sm:p-4 border-t border-border bg-card flex-shrink-0">
          <Button variant="outline" onClick={onClose}>
            Anulează
          </Button>
          <Button onClick={handleCreate} disabled={!canCreate} iconName="MessageSquare">
            Creează conversația
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NewConversationModal;
